import { useEffect, useMemo, useState } from "react";
import {
  MRT_ColumnDef,
  MRT_TableInstance,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { Box } from "@mui/material";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { useSelector } from "../../app/helpers";
import { RootState } from "../../app/rootReducer";
import { BookingType } from "../../features/Booking-Form/typings/booking-inputs";
import { ListTopToolbar } from "../Common/ListTablePartial/List-top-toolbar.partial";
import { EditableCategoryListRowActions } from "../Category/partial/editable-category-row-actions.partial";
import { PropertyTypeFilterCards } from "./PropertyTypeFilterCards.partial";
import { BookingPropertyTypeSwitches } from "./BookingPropertyTypeSwitches.partial";
import { commonLocalization } from "../../functions/tableLocalization";
import TableDeleteConfirmDialog from "../Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import { CategoryTableSlideoutPanel } from "../Category/editable-category-list.partial";
import { useRemoteFetchBookingProperty } from "../../hooks/Remote/BookingProperty/useRemoteFetchBookingProperty";
import { useRemoteUpdateBookingProperty } from "../../hooks/Remote/BookingProperty/useRemoteUpdateBookingProperty";
import { useRemoteCreateBookingProperty } from "../../hooks/Remote/BookingProperty/useRemoteCreateBookingProperty";
import { useRemoteDeleteBookingProperty } from "../../hooks/Remote/BookingProperty/useRemoteDeleteBookingProperty";
import { BookingProperty } from "../FacilityManager/Domain/Types/FloorPlan/BookingProperty.type";
import { validateBookingPropertyRow } from "./editable-booking-property-list.functions";
import { bookingTypesColumnDef } from "./helpers/bookingTypesColumnDef";
import { mapNewRowToPartialBookingProperty } from "./helpers/mapNewRowToPartialBookingProperty";
import { mapUpdatedRowPartialBookingProperty } from "./helpers/mapUpdateRowToPartialBookingProperty";
import { withSeeditMrtInitialState } from "../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../themes/use-seedit-mrt-table-options";

type EditableBookingPropertyListProps = {
  initialPropertyTypes?: string[];
};

export default function EditableBookingPropertyList({
  initialPropertyTypes = []
}: EditableBookingPropertyListProps) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const placeTypes = useSelector((state: RootState) => state.login.userInformation.placeTypes);
  const hasFleet = placeTypes.some(type => type.name === BookingType.VEHICLE);

  const [data, setData] = useState<BookingProperty[]>([]);
  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<number[] | null>(null);
  const [selectedPropertyTypes, setSelectedPropertyTypes] =
    useState<string[]>(initialPropertyTypes);

  const {
    data: remoteBookingProperty,
    refetch: refetchRemoteBookingProperty,
    isLoading: isBookingPropertyLoading,
    isFetching: isBookingPropertyFetching
  } = useRemoteFetchBookingProperty({
    propertyType: selectedPropertyTypes.length > 0 ? selectedPropertyTypes : undefined
  });

  const { mutateAsync: createBookingProperty, status: createBookingPropertyStatus } =
    useRemoteCreateBookingProperty();
  const { mutateAsync: removeBookingProperty, status: removeBookingPropertyStatus } =
    useRemoteDeleteBookingProperty();
  const { mutateAsync: updateBookingProperty, status: updateBookingPropertyStatus } =
    useRemoteUpdateBookingProperty();

  function togglePropertyTypeFilter(type: string) {
    setSelectedPropertyTypes(prev =>
      prev.includes(type) ? prev.filter(t => t !== type) : [...prev, type]
    );
  }

  /** delete an equipment */
  async function handleRowDelete(rowIds: number[]) {
    removeBookingProperty({ ids: rowIds });
  }

  /** adds an equipment */
  const handleRowAdd: MRT_TableOptions<BookingProperty>["onCreatingRowSave"] = async ({
    values,
    table
  }) => {
    const newValidationErrors = validateBookingPropertyRow(values);

    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    setValidationErrors({});

    const newVal = mapNewRowToPartialBookingProperty(values);
    await createBookingProperty(newVal);
    table.setCreatingRow(null); //exit creating mode
  };

  /** updates an equipment */
  const handleRowUpdate: MRT_TableOptions<BookingProperty>["onEditingRowSave"] = async ({
    row,
    values,
    table
  }) => {
    const newValidationErrors = validateBookingPropertyRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    setValidationErrors({});

    const newVal = mapUpdatedRowPartialBookingProperty(values, row.original.id);
    await updateBookingProperty(newVal);
    table.setEditingRow(null); //exit editing mode
  };

  const column = useMemo(
    (): MRT_ColumnDef<BookingProperty>[] => [
      {
        accessorKey: "name",
        header: t("Name"),
        size: 350,
        enableEditing: true,
        muiEditTextFieldProps: {
          onChange: e => {
            if (e.currentTarget.value === "") {
              setValidationErrors({
                ...validationErrors,
                name: undefined
              });
            }
          },
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              name: undefined
            }),
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name
        }
      },
      {
        accessorKey: "description",
        header: t("Description"),
        muiEditTextFieldProps: {
          onChange: e => {
            if (e.currentTarget.value === "") {
              setValidationErrors({
                ...validationErrors,
                description: undefined
              });
            }
          },
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              description: undefined
            }),
          required: true,
          error: !!validationErrors?.description,
          helperText: validationErrors?.description
        },
        size: 600,
        enableEditing: true,
        filterFn: "includesString"
      },
      // Hidden columns: used only for create/edit modal (internalEditComponents 2, 3, 4)
      ...(["usedForWorkplace", "usedForZone", "usedForFleet"] as const).map(
        (accessorKey): MRT_ColumnDef<BookingProperty> => ({
          accessorKey,
          header: {
            usedForWorkplace: t("Workplace"),
            usedForZone: t("Room"),
            usedForFleet: t("DrawerFleet")
          }[accessorKey],
          enableEditing: true,
          enableHiding: true
        })
      ),
      // Single column with type switches (Fleet only when tenant has fleet in placeTypes)
      bookingTypesColumnDef(t, hasFleet)
    ],
    [
      validationErrors,
      remoteBookingProperty,
      i18n.language,
      t,
      updateBookingProperty,
      refetchRemoteBookingProperty,
      hasFleet
    ]
  );
  const seeditMrtOptions = useSeeditMrtTableOptions<BookingProperty>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    columns: column ?? [],
    data: data ?? [],
    getRowId: row => row.id?.toString(),
    onCreatingRowCancel: () => setValidationErrors({}),
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: () => setValidationErrors({}),
    onEditingRowSave: handleRowUpdate,
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    autoResetPageIndex: false,
    enableHiding: true,
    enableRowSelection: true,
    enableEditing: true,
    positionToolbarAlertBanner: "none",
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 },
      columnVisibility: {
        usedForFleet: false,
        usedForWorkplace: false,
        usedForZone: false
      }
    }),
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
      variant: "outlined"
    },
    state: {
      isLoading: isBookingPropertyLoading,
      isSaving:
        (createBookingPropertyStatus ||
          updateBookingPropertyStatus ||
          removeBookingPropertyStatus) === "loading",
      showProgressBars: isBookingPropertyFetching
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noBookingProperty") })
    },
    renderTopToolbar: ({ table }) => (
      <ListTopToolbar
        table={table as unknown as MRT_TableInstance<Record<string, unknown>>}
        title={t("_propertiesPerBookingType_title")}
        createBtnLabel={t("Add Property")}
        isEditable={true}
        disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
        onDelete={() => {
          setIsDeleteModalOpen(true);
          const selected = table.getSelectedRowModel().rows.map(row => row.original.id);
          setDeletionSelected(selected);
        }}
        childComponent={
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              alignItems: "flex-start",
              gap: 2,
              width: "100%"
            }}
          >
            <PropertyTypeFilterCards
              selectedTypes={selectedPropertyTypes}
              onToggleType={togglePropertyTypeFilter}
              onClear={() => setSelectedPropertyTypes([])}
              hasFleet={hasFleet}
            />
          </Box>
        }
      />
    ),
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <CategoryTableSlideoutPanel
        table={table as any}
        row={row as any}
        internalEditComponents={internalEditComponents}
        categoryType={"booking-property"}
        headline={t("_editable_categoryList_Add", { item: t("Property") })}
        typeSection={<BookingPropertyTypeSwitches table={table} row={row} hasFleet={hasFleet} />}
      />
    ),
    renderRowActions: ({ row, table }) => {
      return (
        <EditableCategoryListRowActions
          row={row as any}
          table={table as any}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeletionSelected={setDeletionSelected}
        />
      );
    },
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <CategoryTableSlideoutPanel
        table={table as any}
        row={row as any}
        internalEditComponents={internalEditComponents}
        categoryType={"booking-property"}
        headline={t("_editable_categoryList_Edit", { item: t("Property") })}
        typeSection={<BookingPropertyTypeSwitches table={table} row={row} hasFleet={hasFleet} />}
      />
    )
  });

  useEffect(() => {
    if (!remoteBookingProperty) return;
    setData(
      remoteBookingProperty.map(el => ({
        id: el.id,
        name: el.name,
        description: el.description,
        usedForFleet: el.usedForFleet ?? false,
        usedForWorkplace: el.usedForWorkplace ?? false,
        usedForZone: el.usedForZone ?? false
      }))
    );
  }, [remoteBookingProperty]);

  // after deleting a row, refresh the list
  useEffect(() => {
    if (removeBookingPropertyStatus === "success") {
      const title =
        table.getSelectedRowModel().rows.length > 1 ? "booking property" : "booking properties";

      enqueueSnackbar(t(`Successfully deleted ${title}`), { variant: "success" });
      table.resetRowSelection(); // exit selecting mode
      setIsDeleteModalOpen(false);
      refetchRemoteBookingProperty();
      setDeletionSelected(null);
    }
  }, [removeBookingPropertyStatus]);

  // after adding a row, refresh the list
  useEffect(() => {
    if (createBookingPropertyStatus === "success") refetchRemoteBookingProperty();
  }, [createBookingPropertyStatus]);

  // after updating a eow, refresh the list
  useEffect(() => {
    if (updateBookingPropertyStatus === "success") refetchRemoteBookingProperty();
  }, [updateBookingPropertyStatus]);

  return (
    <>
      <MaterialReactTable table={table} />

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDesc", {
                title: t("_propertiesPerBookingType_title")
              })
            : t("_tableDeletionConfirmationDesc")
        }
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        deleteStatus={removeBookingPropertyStatus}
      />
    </>
  );
}
